import { create } from 'zustand';
import type { SupportedLanguage, AppModule } from '../types';
import type { AmlSection } from '../features/aml/types/aml';
import type { SalesSection } from '../features/sales/types/sales';

export type SettingsScope = 'organization' | 'platform';

const AML_SECTIONS: AmlSection[] = ['customers', 'transactions', 'reports', 'pivot-report', 'sanctions', 'clients'];
const SALES_SECTIONS: SalesSection[] = ['pipeline', 'archived', 'generator', 'settings'];
const APP_MODULES: AppModule[] = ['financial-forensics', 'performance', 'org-management', 'aml-compliance', 'sales'];

function readSession<T>(key: string, fallback: T, valid?: T[]): T {
  try {
    const raw = sessionStorage.getItem(key);
    if (raw === null) return fallback;
    const parsed = JSON.parse(raw) as T;
    if (valid && !valid.includes(parsed)) return fallback;
    return parsed;
  } catch {
    return fallback;
  }
}

function writeSession(key: string, value: unknown) {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore storage errors (private browsing quota, etc.)
  }
}

interface UIState {
  activeModule: AppModule;
  sidebarOpen: boolean;
  showUpload: boolean;
  userMenuOpen: boolean;
  language: SupportedLanguage;
  showLogoutDialog: boolean;
  showDeleteDialog: boolean;
  analysisToDelete: any;
  deletingAnalysis: boolean;
  showComparisonSelector: boolean;
  // Settings T2 sidebar scope
  settingsScope: SettingsScope;
  // AML T2 sidebar section
  amlSection: AmlSection;
  // Performance T2 sidebar section
  performanceSection: string;
  // Sales T2 sidebar section
  salesSection: SalesSection;
  // Discovery portal
  discoveryPortalOpen: boolean;
  // Survey portal
  surveyPortalOpen: boolean;
}

interface UIActions {
  setActiveModule: (module: AppModule) => void;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  setShowUpload: (show: boolean) => void;
  setUserMenuOpen: (open: boolean) => void;
  toggleUserMenu: () => void;
  setLanguage: (lang: SupportedLanguage) => void;
  openLogoutDialog: () => void;
  closeLogoutDialog: () => void;
  openDeleteDialog: (analysis: any) => void;
  closeDeleteDialog: () => void;
  setDeletingAnalysis: (deleting: boolean) => void;
  setShowComparisonSelector: (show: boolean) => void;
  setSettingsScope: (scope: SettingsScope) => void;
  setAmlSection: (section: AmlSection) => void;
  setPerformanceSection: (section: string) => void;
  setSalesSection: (section: SalesSection) => void;
  setDiscoveryPortalOpen: (open: boolean) => void;
  setSurveyPortalOpen: (open: boolean) => void;
}

export const useUIStore = create<UIState & UIActions>((set) => ({
  // State — restored from sessionStorage so the user returns to the same section on reload
  activeModule: readSession<AppModule>('ui.activeModule', 'financial-forensics', APP_MODULES),
  sidebarOpen: true,
  showUpload: false,
  userMenuOpen: false,
  language: 'en',
  showLogoutDialog: false,
  showDeleteDialog: false,
  analysisToDelete: null,
  deletingAnalysis: false,
  showComparisonSelector: false,
  settingsScope: readSession<SettingsScope>('ui.settingsScope', 'organization', ['organization', 'platform']),
  amlSection: readSession<AmlSection>('ui.amlSection', 'customers', AML_SECTIONS),
  performanceSection: readSession<string>('ui.performanceSection', 'my-kpis'),
  salesSection: readSession<SalesSection>('ui.salesSection', 'pipeline', SALES_SECTIONS),
  discoveryPortalOpen: false,
  surveyPortalOpen: false,

  // Actions
  setActiveModule: (module) => set((state) => {
    writeSession('ui.activeModule', module);
    const switching = state.activeModule !== module;
    return {
      activeModule: module,
      // Only reset sub-sections when actually switching to a new module
      ...(switching && module === 'org-management' ? { settingsScope: 'organization' as const } : {}),
      ...(switching && module === 'aml-compliance' ? { amlSection: 'customers' as const } : {}),
      ...(switching && module === 'performance' ? { performanceSection: 'my-kpis' } : {}),
      ...(switching && module === 'sales' ? { salesSection: 'pipeline' as const } : {}),
    };
  }),
  toggleSidebar: () => set((s) => ({ sidebarOpen: !s.sidebarOpen })),
  setSidebarOpen: (open) => set({ sidebarOpen: open }),
  setShowUpload: (show) => set({ showUpload: show }),
  setUserMenuOpen: (open) => set({ userMenuOpen: open }),
  toggleUserMenu: () => set((s) => ({ userMenuOpen: !s.userMenuOpen })),
  setLanguage: (lang) => set({ language: lang }),
  openLogoutDialog: () => set({ showLogoutDialog: true, userMenuOpen: false }),
  closeLogoutDialog: () => set({ showLogoutDialog: false }),
  openDeleteDialog: (analysis) => set({ analysisToDelete: analysis, showDeleteDialog: true }),
  closeDeleteDialog: () => set({ showDeleteDialog: false, analysisToDelete: null }),
  setDeletingAnalysis: (deleting) => set({ deletingAnalysis: deleting }),
  setShowComparisonSelector: (show) => set({ showComparisonSelector: show }),
  setSettingsScope: (scope) => { writeSession('ui.settingsScope', scope); set({ settingsScope: scope }); },
  setAmlSection: (section) => { writeSession('ui.amlSection', section); set({ amlSection: section }); },
  setPerformanceSection: (section) => { writeSession('ui.performanceSection', section); set({ performanceSection: section }); },
  setSalesSection: (section) => { writeSession('ui.salesSection', section); set({ salesSection: section }); },
  setDiscoveryPortalOpen: (open) => set({ discoveryPortalOpen: open }),
  setSurveyPortalOpen: (open) => set({ surveyPortalOpen: open }),
}));
